import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { ArrowLeft, Package } from "lucide-react";
import PayPalCheckout from "../components/PayPalCheckout";

export default function DesignDetail() {
  const { name } = useParams();
  const decodedName = decodeURIComponent(name || "");
  const nav = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [variantId, setVariantId] = useState(null);
  const [qty, setQty] = useState(1);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).finally(() => setLoading(false));
  }, []);

  const matches = useMemo(() => {
    const norm = (s) => s.toLowerCase().replace(/[^a-z0-9]/g, "");
    const k = norm(decodedName);
    return products.filter((p) => norm(p.name).includes(k));
  }, [products, decodedName]);

  const cover = matches[0]?.image_url;
  const activeVariant = useMemo(
    () => selected?.variants?.find((v) => v.variant_id === variantId) || selected?.variants?.[0],
    [selected, variantId]
  );

  const openProduct = (p) => {
    setSelected(p);
    setVariantId(p.variants?.[0]?.variant_id ?? null);
    setQty(1);
  };

  const handleSuccess = (data) => {
    setSelected(null);
    nav(`/payment-success?order_id=${data.order_id}`);
  };

  return (
    <div className="min-h-screen max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-20" data-testid="design-detail-page">
      <Link to="/designs" className="inline-flex items-center gap-2 text-[#8D9688] hover:text-[#DD5422] uppercase tracking-widest text-xs font-anton mb-6">
        <ArrowLeft className="w-4 h-4" /> All Designs
      </Link>

      <div className="grid md:grid-cols-2 gap-8 mb-12">
        <div className="aspect-square bg-[#151A14] border-2 border-[#2C332A] overflow-hidden">
          {cover ? (
            <img src={cover} alt={decodedName} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-[#2C332A] font-anton text-9xl">ER</div>
          )}
        </div>
        <div className="flex flex-col justify-center">
          <div className="font-anton uppercase tracking-[0.3em] text-[#DD5422] text-sm mb-2">/ Design</div>
          <h1 className="font-anton text-5xl md:text-6xl uppercase tracking-tight leading-none">{decodedName}</h1>
          <p className="text-[#8D9688] mt-4 max-w-lg">
            One design. Multiple ways to wear it. Pick the product you want below — sizes, colors, and Stripe-secure checkout.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <div className="border-2 border-[#2C332A] px-4 py-2">
              <div className="text-[10px] uppercase tracking-widest text-[#8D9688]">Products</div>
              <div className="font-anton text-2xl">{matches.length}</div>
            </div>
            <div className="border-2 border-[#2C332A] px-4 py-2">
              <div className="text-[10px] uppercase tracking-widest text-[#8D9688]">From</div>
              <div className="font-anton text-2xl text-[#DD5422]">
                ${Math.min(...matches.map((m) => m.price_from || 0)).toFixed(2)}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mb-4">
        <div className="font-anton uppercase tracking-[0.3em] text-[#DD5422] text-xs mb-2">/ Available On</div>
        <h2 className="font-anton text-3xl md:text-4xl uppercase tracking-tight">Choose Your Item</h2>
      </div>

      {loading ? (
        <div className="text-[#8D9688] uppercase tracking-widest">Loadin'...</div>
      ) : matches.length === 0 ? (
        <div className="bg-[#151A14] border-2 border-[#2C332A] p-10 text-center text-[#8D9688]" data-testid="design-detail-empty">
          <Package className="w-10 h-10 mx-auto text-[#DD5422] mb-2" />
          No products yet for this design. Check back soon.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {matches.map((p) => (
            <button
              key={p.id}
              data-testid={`product-card-${p.id}`}
              onClick={() => openProduct(p)}
              className="group text-left bg-[#151A14] border-2 border-[#2C332A] hover:border-[#DD5422] transition-all hover:-translate-y-1"
            >
              <div className="relative aspect-square overflow-hidden bg-[#0A0E0B]">
                {p.image_url ? (
                  <img src={p.image_url} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[#2C332A] font-anton text-4xl">ER</div>
                )}
                <div className="absolute top-3 right-3 bg-[#DD5422] text-[#0A0E0B] px-3 py-1 font-anton text-lg">
                  ${p.price_from.toFixed(2)}
                </div>
              </div>
              <div className="p-5">
                <div className="text-xs uppercase tracking-[0.2em] text-[#8D9688]">{p.category}</div>
                <div className="font-anton text-lg uppercase tracking-tight mt-1 line-clamp-2 min-h-[3rem]">{p.name}</div>
              </div>
            </button>
          ))}
        </div>
      )}

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent data-testid="product-modal"
          className="bg-[#151A14] border-2 border-[#2C332A] text-[#E5E5E5] rounded-none max-w-3xl p-0 max-h-[90vh] overflow-y-auto">
          {selected && activeVariant && (
            <div className="grid md:grid-cols-2">
              <div className="aspect-square bg-[#0A0E0B] relative">
                <img src={activeVariant.image_url || selected.image_url} alt={selected.name} className="w-full h-full object-cover" />
              </div>
              <div className="p-6 md:p-8">
                <DialogHeader>
                  <div className="text-xs uppercase tracking-[0.2em] text-[#8D9688]">{selected.category}</div>
                  <DialogTitle className="font-anton text-2xl md:text-3xl uppercase tracking-tight">{selected.name}</DialogTitle>
                </DialogHeader>
                <div className="font-anton text-4xl text-[#DD5422] mt-3">${activeVariant.price.toFixed(2)}</div>
                <p className="text-[#8D9688] mt-4 text-sm">{selected.description}</p>
                {selected.variants.length > 1 && (
                  <div className="mt-6">
                    <div className="text-xs uppercase tracking-widest text-[#8D9688] mb-2">Choose Variant</div>
                    <div className="flex flex-wrap gap-2">
                      {selected.variants.map((v) => (
                        <button key={v.variant_id} data-testid={`variant-${v.variant_id}`}
                          onClick={() => setVariantId(v.variant_id)}
                          className={`px-3 py-2 border-2 font-anton uppercase text-xs tracking-widest ${
                            v.variant_id === activeVariant.variant_id
                              ? "border-[#DD5422] bg-[#DD5422] text-[#0A0E0B]"
                              : "border-[#2C332A] hover:border-[#DD5422] text-[#E5E5E5]"
                          }`}>{v.label}</button>
                      ))}
                    </div>
                  </div>
                )}
                <div className="mt-6">
                  <div className="text-xs uppercase tracking-widest text-[#8D9688] mb-2">Quantity</div>
                  <div className="inline-flex border-2 border-[#2C332A]">
                    <button onClick={() => setQty(Math.max(1, qty - 1))} className="px-4 py-2 hover:bg-[#2C332A]">−</button>
                    <div className="px-6 py-2 font-anton text-lg">{qty}</div>
                    <button onClick={() => setQty(Math.min(10, qty + 1))} className="px-4 py-2 hover:bg-[#2C332A]">+</button>
                  </div>
                </div>
                <div className="mt-6 border-2 border-[#2C332A] p-4 bg-[#0A0E0B]">
                  <div className="flex justify-between items-center mb-3">
                    <div className="text-xs uppercase tracking-widest text-[#8D9688]">Total</div>
                    <div className="font-anton text-2xl text-[#DD5422]">${(activeVariant.price * qty).toFixed(2)}</div>
                  </div>
                  <PayPalCheckout type="merch"
                    payload={{
                      product_id: selected.id,
                      variant_id: activeVariant.variant_id,
                      quantity: qty,
                      origin_url: window.location.origin,
                    }}
                    subtotal={activeVariant.price * qty}
                    onSuccess={handleSuccess} />
                </div>
                <div className="text-xs text-[#8D9688] mt-3 uppercase tracking-widest text-center">Secure PayPal checkout · Printful fulfillment</div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
